/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { dashboardAPI, stkAPI } from '../../lib/api';
import { formatDate, formatRelativeTime, STK_STATUS } from '../../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import { Progress } from '../../components/ui/progress';
import STKPendingDocRequests from '../../components/STKPendingDocRequests';
import {
  Building2,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Megaphone,
  Plus,
  FileText,
  Users,
  Briefcase,
  Loader2,
  ExternalLink,
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, description, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    yellow: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <Card className="card-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
            {description && (
              <p className="text-sm text-slate-500 mt-1">{description}</p>
            )}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const STKDashboard = () => {
  const { user } = useAuth();
  usePageTitle('STK Paneli');
  const [stats, setStats] = useState(null);
  const [application, setApplication] = useState(null);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, appRes, announcementsRes, meetingsRes] = await Promise.all([
        dashboardAPI.getStats(),
        stkAPI.getApplications({ limit: 1 }),
        dashboardAPI.getRecentAnnouncements(5),
        dashboardAPI.getUpcomingMeetings(5),
      ]);
      
      setStats(statsRes.data);
      setApplication(appRes.data.applications[0] || null);
      setRecentAnnouncements(announcementsRes.data);
      setUpcomingMeetings(meetingsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getStatusBadge = (status) => {
    const statusInfo = STK_STATUS[status] || { label: status, color: 'default' };
    const colorClasses = {
      success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-50 text-amber-700 border-amber-200',
      error: 'bg-red-50 text-red-700 border-red-200',
      info: 'bg-blue-50 text-blue-700 border-blue-200',
      default: 'bg-slate-50 text-slate-700 border-slate-200',
    };
    return <Badge className={`${colorClasses[statusInfo.color]} border`}>{statusInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const applicationProgress = application ? (application.current_step / 9) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="stk-dashboard">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Building2 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">STK Paneli</h1>
              <p className="text-purple-100 mt-1">
                {application?.kurum_adi || `Hoş geldiniz, ${user?.name} ${user?.surname}`}
              </p>
            </div>
          </div>
          {application && (
            <div className="hidden md:block">
              {getStatusBadge(application.status)}
            </div>
          )}
        </div>
      </div>

      {/* Bekleyen Belge Talepleri (Yonetimden gelen sonradan istenen belgeler) */}
      <STKPendingDocRequests />

      {/* Application Status Card - If has application */}
      {application ? (
        <Card className="card-shadow border-purple-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Başvuru Durumu</CardTitle>
                <CardDescription>{application.kurum_adi}</CardDescription>
              </div>
              <Link to={`/panel/stk/${application.id}`}>
                <Button variant="outline" size="sm">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Detaylar
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Başvuru Durumu</span>
                {getStatusBadge(application.status)}
              </div>
              
              {application.status === 'taslak' && (
                <>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Tamamlanma Oranı</span>
                      <span className="font-medium">{Math.round(applicationProgress)}%</span>
                    </div>
                    <Progress value={applicationProgress} className="h-2" />
                  </div>
                  <Link to="/panel/stk/basvuru">
                    <Button className="w-full bg-purple-600 hover:bg-purple-700">
                      Başvuruya Devam Et
                    </Button>
                  </Link>
                </>
              )}
              
              {application.status === 'inceleniyor' && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">Başvurunuz İnceleniyor</p>
                      <p className="text-sm text-blue-700">Başvurunuz yetkili birimler tarafından değerlendiriliyor.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {application.status === 'eksik_belge' && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-900">Eksik Belge</p>
                      <p className="text-sm text-amber-700">Başvurunuz için ek belge istenmektedir.</p>
                    </div>
                  </div>
                  <Link to="/panel/stk/basvuru">
                    <Button className="w-full mt-3 bg-amber-600 hover:bg-amber-700">
                      Belgeleri Tamamla
                    </Button>
                  </Link>
                </div>
              )}
              
              {application.status === 'onaylandi' && (
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="font-medium text-emerald-900">Başvurunuz Onaylandı!</p>
                      <p className="text-sm text-emerald-700">Kent Konseyi'ne üye olarak katılabilirsiniz.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* No Application - CTA Card */
        <Card className="card-shadow border-purple-200 bg-purple-50/30">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900">STK Başvurusu Yapın</h3>
            <p className="text-slate-600 mt-2 max-w-md mx-auto">
              Sivil toplum kuruluşunuzu Kent Konseyi'ne kaydedin ve şehrinizin geleceğine katkıda bulunun.
            </p>
            <Link to="/panel/stk/basvuru">
              <Button className="mt-6 bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Başvuru Yap
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Stats - Only for approved STKs */}
      {application?.status === 'onaylandi' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Taleplerim"
            value={stats?.total_tickets || 0}
            icon={FileText}
            color="blue"
          />
          <StatCard
            title="Bekleyen"
            value={stats?.open_tickets || 0}
            icon={Clock}
            color="yellow"
          />
          <StatCard
            title="Çözülen"
            value={stats?.resolved_tickets || 0}
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title="Toplantılar"
            value={upcomingMeetings.length}
            icon={Calendar}
            color="purple"
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Meetings */}
        <Card className="card-shadow">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Yaklaşan Toplantılar</CardTitle>
              <CardDescription>Katılabileceğiniz toplantılar</CardDescription>
            </div>
            <Link to="/panel/toplantilar">
              <Button variant="ghost" size="sm">
                Tümü <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {upcomingMeetings.length > 0 ? (
              <div className="space-y-3">
                {upcomingMeetings.map((meeting) => (
                  <Link
                    key={meeting.id}
                    to={`/panel/toplantilar/${meeting.id}`}
                    className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors border"
                  >
                    <div className="p-2 rounded-lg bg-blue-50 flex-shrink-0">
                      <Calendar className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{meeting.title}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatDate(meeting.date)} • {meeting.time}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Yaklaşan toplantı bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Announcements */}
        <Card className="card-shadow">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Duyurular</CardTitle>
              <CardDescription>Kent Konseyi duyuruları</CardDescription>
            </div>
            <Link to="/panel/duyurular">
              <Button variant="ghost" size="sm">
                Tümü <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentAnnouncements.length > 0 ? (
              <div className="space-y-3">
                {recentAnnouncements.map((announcement) => (
                  <Link
                    key={announcement.id}
                    to={`/panel/duyurular/${announcement.id}`}
                    className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors border"
                  >
                    <div className="p-2 rounded-lg bg-amber-50 flex-shrink-0">
                      <Megaphone className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{announcement.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{formatRelativeTime(announcement.created_at)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Henüz duyuru bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* STK Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-shadow">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Çalışma Grupları</h3>
            <p className="text-sm text-slate-500 mt-2">
              İlgi alanınıza göre çalışma gruplarına katılın
            </p>
          </CardContent>
        </Card>
        <Card className="card-shadow">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Projeler</h3>
            <p className="text-sm text-slate-500 mt-2">
              Kent Konseyi projelerine katkı sağlayın
            </p>
          </CardContent>
        </Card>
        <Card className="card-shadow">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Toplantılar</h3>
            <p className="text-sm text-slate-500 mt-2">
              Meclis ve kurul toplantılarına katılın
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default STKDashboard;
